// Privacy-safe client environment snapshot.
// No PII, no persistent identifiers, no network calls. Never throws.

import { Capacitor } from "@capacitor/core";

export type AppSurface = "web" | "capacitor-ios" | "capacitor-android" | "capacitor-other";

export interface ClientEnv {
  device_type: "mobile" | "tablet" | "desktop";
  browser_name: string;
  browser_version: string;
  os_name: string;
  os_version: string;
  app_surface: AppSurface;
  viewport: string;
  screen: string;
  dpr: number;
  touch_capable: boolean;
  connection_type: string | null;
  effective_type: string | null;
  downlink_mbps: number | null;
  language: string;
  timezone: string;
  color_scheme: "dark" | "light" | "unknown";
  online: boolean;
}

const safe = <T>(fn: () => T, fallback: T): T => {
  try {
    return fn();
  } catch {
    return fallback;
  }
};

const detectBrowser = (ua: string): { name: string; version: string } => {
  // Order matters: check Edge/Opera before Chrome, Chrome before Safari.
  const patterns: Array<[string, RegExp]> = [
    ["Edge", /Edg(?:e|A|iOS)?\/([\d.]+)/],
    ["Opera", /(?:Opera|OPR)\/([\d.]+)/],
    ["Samsung", /SamsungBrowser\/([\d.]+)/],
    ["Firefox", /Firefox\/([\d.]+)/],
    ["Chrome", /Chrome\/([\d.]+)/],
    ["Safari", /Version\/([\d.]+).*Safari/],
    ["WebView", /(?:wv\)|; wv)/],
  ];
  for (const [name, re] of patterns) {
    const m = ua.match(re);
    if (m) return { name, version: m[1] ?? "" };
  }
  return { name: "Unknown", version: "" };
};

const detectOS = (ua: string): { name: string; version: string } => {
  const tests: Array<[string, RegExp]> = [
    ["iOS", /(?:iPhone|iPad|iPod).*?OS ([\d_]+)/],
    ["Android", /Android ([\d.]+)/],
    ["Windows", /Windows NT ([\d.]+)/],
    ["macOS", /Mac OS X ([\d_]+)/],
    ["Linux", /Linux/],
  ];
  for (const [name, re] of tests) {
    const m = ua.match(re);
    if (m) return { name, version: (m[1] || "").replace(/_/g, ".") };
  }
  return { name: "Unknown", version: "" };
};

const detectDevice = (ua: string): "mobile" | "tablet" | "desktop" => {
  if (/iPad|Tablet|(Android(?!.*Mobile))/i.test(ua)) return "tablet";
  if (/Mobi|Android|iPhone|iPod/i.test(ua)) return "mobile";
  return "desktop";
};

const detectSurface = (): AppSurface => {
  try {
    if (Capacitor.isNativePlatform()) {
      const p = Capacitor.getPlatform();
      if (p === "ios") return "capacitor-ios";
      if (p === "android") return "capacitor-android";
      return "capacitor-other";
    }
  } catch {
    // ignore
  }
  return "web";
};

let cached: ClientEnv | null = null;

export const getClientEnv = (): ClientEnv => {
  if (cached) return { ...cached, online: safe(() => navigator.onLine, true) };

  const ua = safe(() => navigator.userAgent, "");
  const browser = detectBrowser(ua);
  const os = detectOS(ua);
  // @ts-expect-error non-standard
  const conn = safe(() => navigator.connection ?? navigator.mozConnection ?? navigator.webkitConnection, null);

  cached = {
    device_type: detectDevice(ua),
    browser_name: browser.name,
    browser_version: browser.version.slice(0, 20),
    os_name: os.name,
    os_version: os.version.slice(0, 20),
    app_surface: detectSurface(),
    viewport: safe(() => `${window.innerWidth}x${window.innerHeight}`, ""),
    screen: safe(() => `${window.screen?.width ?? 0}x${window.screen?.height ?? 0}`, ""),
    dpr: safe(() => Math.round((window.devicePixelRatio ?? 1) * 100) / 100, 1),
    touch_capable: safe(
      () => (navigator.maxTouchPoints ?? 0) > 0 || "ontouchstart" in window,
      false,
    ),
    connection_type: safe(() => conn?.type ?? null, null),
    effective_type: safe(() => conn?.effectiveType ?? null, null),
    downlink_mbps: safe(() => (typeof conn?.downlink === "number" ? conn.downlink : null), null),
    language: safe(() => navigator.language, ""),
    timezone: safe(() => Intl.DateTimeFormat().resolvedOptions().timeZone, ""),
    color_scheme: safe(
      () =>
        window.matchMedia?.("(prefers-color-scheme: dark)").matches
          ? "dark"
          : window.matchMedia?.("(prefers-color-scheme: light)").matches
            ? "light"
            : "unknown",
      "unknown",
    ),
    online: safe(() => navigator.onLine, true),
  };
  return cached;
};

// Flattened variant safe to inline in analytics event_data / metadata payloads.
export const getClientEnvPayload = (): Record<string, unknown> => {
  const e = getClientEnv();
  return {
    device_type: e.device_type,
    browser_name: e.browser_name,
    browser_version: e.browser_version,
    os_name: e.os_name,
    os_version: e.os_version,
    app_surface: e.app_surface,
    viewport: e.viewport,
    screen: e.screen,
    dpr: e.dpr,
    touch_capable: e.touch_capable,
    connection_type: e.connection_type,
    effective_type: e.effective_type,
    downlink_mbps: e.downlink_mbps,
    language: e.language,
    timezone: e.timezone,
    color_scheme: e.color_scheme,
    online: e.online,
  };
};
